import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import '../../assets/stylesheets/LatestReportBadge.css';
import { formatSavedDate, isValidLatestReport } from './latestReportHelpers';
import {
  getLatestReport,
  LATEST_REPORT_EVENT,
  LATEST_REPORT_STORAGE_KEY
} from './latestReportStorage';
import { EVENT_NAMES, PARAM_KEYS, trackEvent } from '../../analytics';

function getBadgeTitle(report) {
  const pieces = ['Latest saved report'];

  if (report?.title) {
    pieces.push(report.title);
  }

  if (report?.savedAt) {
    pieces.push(`Saved ${new Date(report.savedAt).toLocaleString()}`);
  }

  return pieces.join(' • ');
}

export default function LatestReportBadge({ placement = 'unknown' }) {
  const [latestReport, setLatestReport] = useState(() => getLatestReport());

  useEffect(() => {
    function syncLatestReport() {
      setLatestReport(getLatestReport());
    }

    function handleStorage(event) {
      if (event.key && event.key !== LATEST_REPORT_STORAGE_KEY) {
        return;
      }

      syncLatestReport();
    }

    syncLatestReport();
    window.addEventListener('storage', handleStorage);
    window.addEventListener(LATEST_REPORT_EVENT, syncLatestReport);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener(LATEST_REPORT_EVENT, syncLatestReport);
    };
  }, []);

  if (!isValidLatestReport(latestReport)) {
    return null;
  }

  const savedDate = formatSavedDate(latestReport.savedAt);
  const projectType = latestReport.projectType
    ? latestReport.projectType.replace(/[-_]+/g, ' ')
    : '';

  return (
    <Link
      to={latestReport.reportUrl}
      className="latestReportBadge"
      title={getBadgeTitle(latestReport)}
      aria-label="View your latest saved report"
      onClick={() => {
        trackEvent(EVENT_NAMES.LATEST_REPORT_BADGE_CLICKED, {
          [PARAM_KEYS.CATEGORY_KEY]: latestReport.projectType,
          [PARAM_KEYS.PROJECT_GUID]: latestReport.projectGuid,
          [PARAM_KEYS.PLACEMENT]: placement,
        });
      }}
    >
      <span className="latestReportBadgeIcon" aria-hidden="true">
        <svg viewBox="0 0 20 20" width="14" height="14" fill="currentColor">
          <path d="M5 2.5A2.5 2.5 0 0 0 2.5 5v10A2.5 2.5 0 0 0 5 17.5h10a2.5 2.5 0 0 0 2.5-2.5V8.207a2.5 2.5 0 0 0-.732-1.768l-3.207-3.207A2.5 2.5 0 0 0 11.793 2.5H5Zm6.25 1.75v3a1 1 0 0 0 1 1h3V15A.75.75 0 0 1 14.5 15.75h-9A.75.75 0 0 1 4.75 15V5c0-.414.336-.75.75-.75h5.75Zm-4 7a.75.75 0 0 0 0 1.5h5.5a.75.75 0 0 0 0-1.5h-5.5Zm0-3a.75.75 0 0 0 0 1.5h2.5a.75.75 0 0 0 0-1.5h-2.5Z" />
        </svg>
      </span>

      <span className="latestReportBadgeBody">
        <span className="latestReportBadgeLabel">View Report</span>
        {(projectType || savedDate) && (
          <span className="latestReportBadgeMeta" aria-hidden="true">
            {projectType ? <span className="latestReportBadgeType">{projectType}</span> : null}
            {projectType && savedDate ? <span>•</span> : null}
            {savedDate ? <span>{savedDate}</span> : null}
          </span>
        )}
      </span>

      <span className="latestReportBadgeArrow" aria-hidden="true">›</span>
    </Link>
  );
}